import { useCallback, useContext } from 'react'
import type { ChangeEvent } from 'react'
import { useIntl } from 'react-intl'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import type { FlightDropdownOption } from 'components/Flights/types'
import type { FlightsClassTypes } from 'constants/flight'
import FareDropdown from './FareDropdown/FareDropdown'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultiTicketFlightsEnabled, isMultiTicketMultiCabinFlightsEnabled } from 'selectors/flightsMultiTicketSelectors'

export function MultiTicketToggle({ className }: { className?: string }) {
  const enabled = useAppSelector(isMultiTicketFlightsEnabled)
  const intl = useIntl()
  const { multiTicket } = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: FlightSearchWidgetActions.SET_MULTI_TICKET, enabled: event.target.checked })
  }, [dispatch])
  if (!enabled) return null
  return <CheckboxInput className={className} checked={!!multiTicket} onChange={handleChange}>
    {intl.formatMessage({
      id: 'uQanAC3PNNcAijc5luaa',
      defaultMessage: 'Choose tickets separately',
      description: 'Toggle to choose independent flights and fare families for each leg of a multi-city trip',
    })}
  </CheckboxInput>
}

export function MultiTicketLegCabin({ flightId, className }: { flightId: string, className?: string }) {
  const multiCabinEnabled = useAppSelector(isMultiTicketMultiCabinFlightsEnabled)
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const { fareCabins } = useFlightFareLabels()
  const isMultiTicketMultiCabinEnabled = multiCabinEnabled && state.multiTicket
  const handleChange = useCallback((option: FlightDropdownOption<FlightsClassTypes>) => {
    dispatch({ type: FlightSearchWidgetActions.SET_LEG_CABIN, id: flightId, fareClass: option.value })
  }, [dispatch, flightId])
  if (!isMultiTicketMultiCabinEnabled) return null

  const fareClass = state.flights.find(flight => flight.id === flightId)?.fareClass ?? state.fareCabin.value
  return <FareDropdown className={`cabin-input-area ${className ?? ''}`} options={fareCabins} selectedOption={fareCabins.find(option => option.value === fareClass)} onOptionChange={handleChange} />
}
